import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CustomerGroup, SaveCustomerGroupDto } from "@/lib/api";
import {
  getCustomerRegistrationConflictCode,
  getCustomerRegistrationErrorMessage,
  getFieldErrors,
} from "@/lib/customerRegistrationError";
import {
  customerGroupFormSchema,
  CustomerGroupFormValues,
  toCustomerGroupFormValues,
  toSaveCustomerGroupDto,
} from "@/lib/validators/customerGroup";

// 409 da API ligado ao campo que causou o conflito.
const CONFLICT_FIELDS: Record<string, keyof CustomerGroupFormValues> = {
  customer_group_code_taken: "code",
  customer_group_magento_id_taken: "magentoGroupId",
};

interface CustomerGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Grupo em edição; ausente = cadastro. */
  group?: CustomerGroup | null;
  isSubmitting: boolean;
  onSave: (group: SaveCustomerGroupDto) => Promise<unknown>;
}

export function CustomerGroupDialog({ open, onOpenChange, group, isSubmitting, onSave }: CustomerGroupDialogProps) {
  const isEditing = !!group;
  const form = useForm<CustomerGroupFormValues>({
    resolver: zodResolver(customerGroupFormSchema),
    defaultValues: toCustomerGroupFormValues(group),
  });

  useEffect(() => {
    if (open) form.reset(toCustomerGroupFormValues(group));
  }, [open, group, form]);

  const submit = async (values: CustomerGroupFormValues) => {
    try {
      await onSave(toSaveCustomerGroupDto(values));
      onOpenChange(false);
    } catch (error) {
      for (const fieldError of getFieldErrors(error)) {
        if (fieldError.field in values) {
          form.setError(fieldError.field as keyof CustomerGroupFormValues, { message: fieldError.message });
        }
      }
      const message = getCustomerRegistrationErrorMessage(error, "Não foi possível salvar o grupo.");
      const conflictField = CONFLICT_FIELDS[getCustomerRegistrationConflictCode(error) ?? ""];
      if (conflictField) form.setError(conflictField, { message });
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar grupo de cliente" : "Novo grupo de cliente"}</DialogTitle>
          <DialogDescription>
            {isEditing && (group?.clientCount ?? 0) > 0
              ? `Mudar o código atualiza também os ${group?.clientCount} cliente(s) deste grupo.`
              : "O código identifica o grupo nos pedidos e precisa ser único."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Código</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus placeholder="Ex.: Escola Parceira" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome (opcional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Sem nome, usa o código" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="magentoGroupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID do Magento (opcional)</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" placeholder="Só para grupos que vieram do Magento" />
                  </FormControl>
                  <FormDescription>Grupos criados no portal não têm ID do Magento.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : "Salvar grupo"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
